
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SecurityIcon, CollateralIcon } from "../BorrowingsIcons";
import { toast } from "sonner";
import { formatCurrency } from "./utils";

interface CollateralManagementProps {
  loan: any;
}

// Sample portfolio loan data - representing NBFC's loan book
const portfolioLoans = [
  { id: 101, borrower: "Sharma Enterprises", amount: 128000, riskRating: "Low", product: "Business Loan", maturity: "2025-06-15", allocated: false },
  { id: 102, borrower: "Bhatia Trading Co.", amount: 235000, riskRating: "Medium", product: "Working Capital", maturity: "2024-12-30", allocated: false },
  { id: 103, borrower: "Verma Manufacturing", amount: 310000, riskRating: "Low", product: "Equipment Finance", maturity: "2026-03-10", allocated: false },
  { id: 104, borrower: "Singh Textiles", amount: 175000, riskRating: "Low", product: "Business Loan", maturity: "2025-08-22", allocated: false },
  { id: 105, borrower: "Patel Logistics", amount: 420000, riskRating: "Medium", product: "Commercial Vehicle", maturity: "2027-01-05", allocated: false },
  { id: 106, borrower: "Kumar Hospitality", amount: 550000, riskRating: "High", product: "Property Loan", maturity: "2028-11-30", allocated: false },
  { id: 107, borrower: "Gupta Retail", amount: 185000, riskRating: "Low", product: "Working Capital", maturity: "2025-04-18", allocated: false },
  { id: 108, borrower: "Joshi Electronics", amount: 275000, riskRating: "Medium", product: "Business Loan", maturity: "2026-07-12", allocated: false },
];

// Sample collateral data based on loan type
const getSampleCollaterals = (loanType: string, loanId: number) => {
  if (loanType.toLowerCase().includes("msme")) {
    return [
      { 
        id: 1, 
        type: "MSME Loan Portfolio", 
        description: "MSME Loans - Urban Region", 
        value: 12500000, 
        currency: "USD", 
        coverageRatio: 1.25, 
        status: "Active", 
        loanId,
        allocatedLoans: [
          portfolioLoans[0],
          portfolioLoans[1],
          portfolioLoans[2],
          portfolioLoans[3]
        ].map(loan => ({...loan, allocated: true}))
      }
    ];
  } else if (loanType.toLowerCase().includes("term")) {
    return [
      { 
        id: 2, 
        type: "SME Loan Portfolio", 
        description: "Manufacturing Sector Loans", 
        value: 5000000, 
        currency: "USD", 
        coverageRatio: 2.0, 
        status: "Active", 
        loanId,
        allocatedLoans: [
          portfolioLoans[4],
          portfolioLoans[5]
        ].map(loan => ({...loan, allocated: true}))
      }
    ];
  } else {
    return [
      { 
        id: 4, 
        type: "Retail Loan Portfolio", 
        description: "Personal & Consumer Loans", 
        value: loanType.toLowerCase().includes("working") ? 1500000 : 5000000, 
        currency: "USD", 
        coverageRatio: loanType.toLowerCase().includes("working") ? 1.5 : 1.25, 
        status: "Active", 
        loanId,
        allocatedLoans: [
          portfolioLoans[6],
          portfolioLoans[7]
        ].map(loan => ({...loan, allocated: true}))
      }
    ];
  }
};

export const CollateralManagement: React.FC<CollateralManagementProps> = ({ loan }) => {
  const [collaterals, setCollaterals] = useState(getSampleCollaterals(loan.instrumentType, loan.id));
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCollateral, setSelectedCollateral] = useState<any>(null);
  const [showAllocateDialog, setShowAllocateDialog] = useState(false);
  const [availableLoans, setAvailableLoans] = useState(portfolioLoans.filter(loan => !loan.allocated));
  const [selectedLoans, setSelectedLoans] = useState<number[]>([]);
  const [newCollateral, setNewCollateral] = useState({
    type: "MSME Loan Portfolio",
    description: "",
    value: 0,
    currency: "USD",
  });

  const handleAddCollateral = () => {
    if (!newCollateral.description || newCollateral.value <= 0) {
      toast.error("Please fill all required fields");
      return;
    }

    const newId = Math.max(0, ...collaterals.map(c => c.id)) + 1;
    
    const collateral = {
      id: newId, 
      type: newCollateral.type,
      description: newCollateral.description,
      value: newCollateral.value,
      currency: newCollateral.currency,
      coverageRatio: newCollateral.value / loan.amount,
      status: "Active",
      loanId: loan.id,
      allocatedLoans: []
    };

    setCollaterals([...collaterals, collateral]);
    setShowAddDialog(false);
    toast.success("Collateral added successfully");
    
    // Reset form
    setNewCollateral({
      type: "MSME Loan Portfolio",
      description: "",
      value: 0,
      currency: "USD",
    });
  };

  const showCollateralDetails = (collateral: any) => {
    setSelectedCollateral(collateral);
    setShowDetailsDialog(true);
  };

  const handleAllocateLoans = (collateral: any) => {
    setSelectedCollateral(collateral);
    setSelectedLoans([]);
    setShowAllocateDialog(true);
  };

  const handleLoanSelectionToggle = (loanId: number) => {
    if (selectedLoans.includes(loanId)) {
      setSelectedLoans(selectedLoans.filter(id => id !== loanId));
    } else {
      setSelectedLoans([...selectedLoans, loanId]);
    }
  };

  const confirmLoanAllocation = () => {
    if (selectedLoans.length === 0) {
      toast.error("Please select at least one loan to allocate");
      return;
    }

    // Get the selected loans
    const loansToAllocate = availableLoans
      .filter(loan => selectedLoans.includes(loan.id))
      .map(loan => ({...loan, allocated: true}));
    
    // Calculate total value of selected loans
    const totalValue = loansToAllocate.reduce((sum, loan) => sum + loan.amount, 0);
    
    // Update the collateral with the allocated loans
    const updatedCollaterals = collaterals.map(coll => {
      if (coll.id === selectedCollateral.id) {
        const updatedAllocatedLoans = [...(coll.allocatedLoans || []), ...loansToAllocate];
        const updatedValue = updatedAllocatedLoans.reduce((sum, loan) => sum + loan.amount, 0);
        return {
          ...coll,
          allocatedLoans: updatedAllocatedLoans,
          value: updatedValue,
          coverageRatio: updatedValue / loan.amount
        };
      }
      return coll;
    });
    
    setCollaterals(updatedCollaterals);
    
    // Update available loans
    setAvailableLoans(availableLoans.filter(loan => !selectedLoans.includes(loan.id)));
    
    setShowAllocateDialog(false);
    toast.success(`${selectedLoans.length} loans allocated successfully`);
    setSelectedLoans([]);
  };

  const renderCollateralValue = (value: number, currency: string, ratio: number) => (
    <div>
      <div className="font-medium">{formatCurrency(value, currency)}</div>
      <div className="text-xs text-muted-foreground">
        Coverage: {(ratio * 100).toFixed(0)}%
      </div>
    </div>
  );

  const getTotalCoverageRatio = () => {
    const totalValue = collaterals.reduce((sum, coll) => sum + coll.value, 0);
    return totalValue / loan.amount;
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <SecurityIcon className="h-4 w-4" />
                Security & Collateral Management
              </CardTitle>
              <CardDescription>
                Allocate loans from your portfolio as security for this borrowing
              </CardDescription>
            </div>
            <Badge 
              className={`${getTotalCoverageRatio() >= 1 ? 'bg-green-500' : 'bg-yellow-500'}`}
            >
              Total Coverage: {(getTotalCoverageRatio() * 100).toFixed(0)}%
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collaterals.length > 0 ? (
                collaterals.map((collateral) => (
                  <TableRow key={collateral.id}>
                    <TableCell className="font-medium">{collateral.type}</TableCell>
                    <TableCell>{collateral.description}</TableCell>
                    <TableCell className="text-right">
                      {renderCollateralValue(
                        collateral.value, 
                        collateral.currency, 
                        collateral.coverageRatio
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-green-50">
                        {collateral.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => showCollateralDetails(collateral)}
                        >
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleAllocateLoans(collateral)}
                        >
                          Allocate Loans
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No security or collateral registered for this borrowing
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter className="bg-muted/50 py-3">
          <Button 
            size="sm" 
            onClick={() => setShowAddDialog(true)}
            className="ml-auto"
          >
            Add Collateral
          </Button>
        </CardFooter>
      </Card>

      {/* Add Collateral Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Collateral</DialogTitle>
            <DialogDescription>
              Register a new security or collateral against this borrowing
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="type">Collateral Type</label>
              <Select
                value={newCollateral.type}
                onValueChange={(value) => setNewCollateral({...newCollateral, type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MSME Loan Portfolio">MSME Loan Portfolio</SelectItem>
                  <SelectItem value="SME Loan Portfolio">SME Loan Portfolio</SelectItem>
                  <SelectItem value="Retail Loan Portfolio">Retail Loan Portfolio</SelectItem>
                  <SelectItem value="Consumer Loan Portfolio">Consumer Loan Portfolio</SelectItem>
                  <SelectItem value="Corporate Guarantee">Corporate Guarantee</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label htmlFor="description">Description</label>
              <Input 
                id="description" 
                value={newCollateral.description}
                onChange={(e) => setNewCollateral({...newCollateral, description: e.target.value})}
                placeholder="Describe the collateral"
              />
            </div>

            <div className="grid gap-2">
              <label htmlFor="value">Initial Value</label>
              <div className="flex gap-2">
                <Input 
                  id="value" 
                  type="number"
                  value={newCollateral.value === 0 ? "" : newCollateral.value}
                  onChange={(e) => setNewCollateral({...newCollateral, value: Number(e.target.value)})}
                  placeholder="Enter value"
                />
                <Select
                  value={newCollateral.currency}
                  onValueChange={(value) => setNewCollateral({...newCollateral, currency: value})}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="INR">INR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>You will be able to allocate specific loans from your portfolio after creating this collateral.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCollateral}>Add Collateral</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collateral Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CollateralIcon className="h-4 w-4" />
              Collateral Details
            </DialogTitle>
            <DialogDescription>
              {selectedCollateral?.type} - {selectedCollateral?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Collateral Value</div>
                <div className="text-lg font-semibold">
                  {selectedCollateral && formatCurrency(selectedCollateral.value, selectedCollateral.currency)}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Coverage Ratio</div>
                <div className="text-lg font-semibold">
                  {selectedCollateral && `${(selectedCollateral.coverageRatio * 100).toFixed(0)}%`}
                </div>
              </div>
            </div>

            {selectedCollateral?.allocatedLoans && selectedCollateral.allocatedLoans.length > 0 && (
              <div className="pt-2">
                <h4 className="font-medium mb-2">Allocated Portfolio Loans</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Loan ID</TableHead>
                      <TableHead>Borrower</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Risk Rating</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedCollateral.allocatedLoans.map((loan: any) => (
                      <TableRow key={loan.id}>
                        <TableCell>{loan.id}</TableCell>
                        <TableCell>{loan.borrower}</TableCell>
                        <TableCell>{loan.product}</TableCell>
                        <TableCell className="text-right">{formatCurrency(loan.amount)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            loan.riskRating === "Low" 
                              ? "bg-green-50" 
                              : loan.riskRating === "Medium" 
                                ? "bg-yellow-50" 
                                : "bg-red-50"
                          }>
                            {loan.riskRating}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Loans Dialog */}
      <Dialog open={showAllocateDialog} onOpenChange={setShowAllocateDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Allocate Loans to Collateral</DialogTitle>
            <DialogDescription>
              Select loans from your portfolio to allocate as collateral
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex justify-between mb-2">
              <h3 className="font-medium">Available Loans</h3>
              <span className="text-sm text-muted-foreground">
                {selectedLoans.length} loans selected
              </span>
            </div>
            
            {availableLoans.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Select</TableHead>
                    <TableHead>Loan ID</TableHead>
                    <TableHead>Borrower</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Risk</TableHead>
                    <TableHead>Maturity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {availableLoans.map((loan) => (
                    <TableRow 
                      key={loan.id} 
                      className={selectedLoans.includes(loan.id) ? "bg-muted" : ""}
                    >
                      <TableCell>
                        <input 
                          type="checkbox" 
                          checked={selectedLoans.includes(loan.id)}
                          onChange={() => handleLoanSelectionToggle(loan.id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </TableCell>
                      <TableCell>{loan.id}</TableCell>
                      <TableCell>{loan.borrower}</TableCell>
                      <TableCell>{loan.product}</TableCell>
                      <TableCell className="text-right">{formatCurrency(loan.amount)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          loan.riskRating === "Low" 
                            ? "bg-green-50" 
                            : loan.riskRating === "Medium" 
                              ? "bg-yellow-50" 
                              : "bg-red-50"
                        }>
                          {loan.riskRating}
                        </Badge>
                      </TableCell>
                      <TableCell>{loan.maturity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 border rounded-md">
                <p className="text-muted-foreground">No available loans to allocate</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllocateDialog(false)}>Cancel</Button>
            <Button 
              onClick={confirmLoanAllocation} 
              disabled={selectedLoans.length === 0 || availableLoans.length === 0}
            >
              Allocate Selected Loans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
